import React, { memo, useCallback } from 'react';
import { DialogActions, Grid, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller } from 'react-hook-form';

import DriverInputs from './driverInputs';
import DriverPassword from './driverPassword';
import SaveChangesButton from './saveChangesButton';
import ChangePasswordButton from './changePasswordButton';
import AddDriverButton from './addDriverButton';
import DriverCheckboxes from './driverCheckboxes';
import Autocomplete from '../../../components/Autocomplete';
import {
  DRIVER,
  MAIN_DRIVERS,
  ODF,
  OTHER_DRIVERS,
} from '../../../constants/strings';
import DriverListOption from '../../addNewRoute/components/DriverListOption';
import { validateDriverName } from '../validators';

const useStyles = makeStyles({
  buttons: {
    padding: `0.5rem 0rem`,
  },
  gridContainer: {
    margin: '0 -8px',
    width: 'calc(100% + 16px)',

    '& > .MuiGrid-item': {
      padding: '8px',
    },
  },
});

const UpdateDriverForm = ({
  isReadOnly,
  isDisabled,
  driver,
  drivers,
  vehicles,
  sections,
  handleSelectDriver,
  isDriverSelected,
  isSubmitting,
  handleAddDriver,
  handleChangePassword,
  handleSelectOdfDriver,
  odfDrivers,
  odfDriver,
  odfDisabled,
  dirty,
  control,
  showMessage,
}) => {
  const styles = useStyles();

  const groupByDrivers = useCallback(
    (option) => (option.isActive ? MAIN_DRIVERS : OTHER_DRIVERS),
    []
  );

  const renderDriverOptionLabel = useCallback(
    (option) => <DriverListOption option={option} />,
    []
  );

  return (
    <>
      <Grid container spacing={2} className={styles.gridContainer}>
        <Grid item xs={7}>
          <Autocomplete
            onChange={handleSelectOdfDriver}
            value={odfDriver?.driverCode}
            label={ODF}
            variant="outlined"
            options={odfDrivers}
            disabled={isReadOnly || odfDisabled}
            dataTestId="select-odfdriver"
            name="odfRouteOwner"
            popperProps={{ placement: 'bottom-start' }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={styles.gridContainer}>
        <Grid item xs={7}>
          {driver.isNewDriver ? (
            <Controller
              name="driverName"
              control={control}
              rules={{
                validate: validateDriverName,
              }}
              render={({ field, formState: { errors } }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Driver Name"
                  placeholder="Forename Surname"
                  variant="outlined"
                  fullWidth
                  error={!!errors.driverName}
                  helperText={errors.driverName?.message}
                  data-testid="driver-name"
                />
              )}
            />
          ) : (
            <Autocomplete
              onChange={handleSelectDriver}
              value={driver?.driverCode}
              label={DRIVER}
              variant="outlined"
              options={drivers}
              renderOptionLabel={renderDriverOptionLabel}
              disabled={isReadOnly}
              dataTestId="select-driver"
              groupBy={groupByDrivers}
              popperProps={{ placement: 'bottom-start' }}
            />
          )}
        </Grid>
        <Grid item xs={5}>
          <Controller
            name="displayName"
            control={control}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                type="text"
                label="Display Name"
                helperText={
                  errors.displayName?.message || 'Driver Display Name'
                }
                error={!!errors.displayName}
                disabled={isDisabled}
                variant="outlined"
                fullWidth
                data-testid="driver-display-name"
              />
            )}
          />
        </Grid>
      </Grid>
      <DriverInputs
        isDisabled={isDisabled}
        vehicles={vehicles}
        sections={sections}
        control={control}
        gridClassName={styles.gridContainer}
        onShowMessage={showMessage}
      />
      <Grid container spacing={2} className={styles.gridContainer}>
        <Grid item xs={6}>
          <DriverCheckboxes isDisabled={isDisabled} control={control} />
        </Grid>
        <Grid item xs={3}>
          <DriverPassword password={driver?.password} control={control} />
        </Grid>
      </Grid>
      <DialogActions className={styles.buttons}>
        <Grid container spacing={2} className={styles.gridContainer}>
          <Grid item xs={4}>
            <AddDriverButton
              handleAddDriver={handleAddDriver}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid
            item
            xs={4}
            container
            justifyContent="center"
            className={styles.gridContainer}
          >
            {isDriverSelected && (
              <ChangePasswordButton
                handleChangePassword={handleChangePassword}
              />
            )}
          </Grid>
          <Grid
            item
            xs={4}
            container
            justifyContent="flex-end"
            className={styles.gridContainer}
          >
            <SaveChangesButton
              disabled={!dirty || isSubmitting || isDisabled}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

export default memo(UpdateDriverForm);
